import { formatDate } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateRangePicker, TinyDatePicker } from 'tiny-date-picker/dist/date-range-picker';

export const DATE_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatePickerComponent),
  multi: true
};

@Component({
  selector: 'sp-datepicker',
  standalone: false,
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [DATE_PICKER_VALUE_ACCESSOR]

})
export class DatePickerComponent implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor {
  datePicker;
  singleDateValue;
  date: Date = new Date();
  start_value = formatDate(this.date, 'longDate', 'en');
  end_value = formatDate(this.date, 'longDate', 'en');
  modalRangeOpen: boolean = false;

  @Input() offset: string = 'right';

  @Output() onDateChange = new EventEmitter<any>();

  @Input() range: boolean = false;
  @Input() noValue: boolean = false;
  @Input() title: string = 'Start Date';
  @Input() to_title: string = 'End Date';
  @Input() placeholder: string = 'Pick Date';
  @Input() formatType: string = 'longDate';
  @Input() label: string;
  @Input() size: string = 'default';
  @Input() options: DateOptionsInterface = {
    mode: 'dp-below',
    // hilightedDate: null,
    min: null,
    max: null,
    dayOffset: null,
    format(date) {
      return formatDate(date, 'longDate', 'en');
    },
    lang: {
      days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      today: 'Today',
      clear: 'Clear',
      close: 'Close'
    }
  };

  @Input() to_options = this.options;

  onChange: any = () => {};

  format = date => {
    try {
      return formatDate(date, this.formatType, 'en');
    } catch (e) {
      return null;
    }
  };

  @Input() parse = str => {
    const date: any = new Date(str);
    return isNaN(date) ? new Date() : date;
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (this.formatType) {
      if (changes?.formatType?.currentValue) {
        this.options.format = date => formatDate(date, changes.formatType.currentValue, 'en');
      }
    }
  }

  constructor(private el: ElementRef, private zone: NgZone) {}

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      let input = this.el.nativeElement.querySelector('.sp-datepicker-input');
      const { body } = document;
      let eventType = 'select';
      if (this.range) {
        this.options.appendTo = this.el.nativeElement.querySelector('.dr-cal-start');
        this.to_options.appendTo = this.el.nativeElement.querySelector('.dr-cal-end');
        this.datePicker = DateRangePicker(input, {
          startOpts: this.options,
          endOpts: this.to_options
        });
        eventType = 'statechange';
      } else {
        input = this.el.nativeElement.querySelector('.single-sp-datepicker');
        this.options.appendTo = this.el.nativeElement.querySelector('.datepicker-containter');
        this.datePicker = TinyDatePicker(input, this.options);
      }
      this.datePicker.on(eventType, (_, picker) => {
        let date = picker.state;
        if (this.range) {
          date = {
            start: picker.state.start,
            end: picker.state.end
          };
        }
        this.zone.run(() => {
          this.start_value = this.format(date.start);
          this.end_value = this.format(date.end);
          let value: any = this.start_value;
          value = date.hilightedDate;
          if (this.range) {
            value = {
              date: this.start_value,
              start: this.start_value,
              end: this.end_value
            };
          }
          this.onChange(value);
          this.onDateChange.emit(value);
        });
        return date;
      });

      body.addEventListener('click', event => {
        const eventPath = this.getEventPath(event);
        // changed the event.target to the event['path']
        const matchingParentElement = this.getMatchingParents(eventPath, 'SP-DATEPICKER');
        let isDatepicker = false;
        if (matchingParentElement.length > 0) {
          isDatepicker = true;
        }
        if (!isDatepicker) {
          this.zone.run(() => {
            this.closeDatePicker();
          });
        }
      });
    });
  }

  getMatchingParents(elem, selector) {
    if (!Element.prototype.matches) {
      Element.prototype.matches =
        Element.prototype['matchesSelector'] ||
        Element.prototype['mozMatchesSelector'] ||
        Element.prototype['msMatchesSelector'] ||
        Element.prototype['oMatchesSelector'] ||
        Element.prototype['webkitMatchesSelector'] ||
        function(s) {
          const matches = (this.document || this.ownerDocument).querySelectorAll(s);
          let i = matches.length;
          while (--i >= 0 && matches.item(i) !== this) {}
          return i > -1;
        };
    }

    // Setup parents array
    const parents = [];
    for (let i = 0; i < elem.length; i++) {
      const node = elem[i];
      if (selector) {
        if (selector === node.nodeName) {
          // Add matching parents to array
          parents.push(elem);
        }
      } else {
        parents.push(elem);
      }
    }
    return parents;
  }

  openDatePicker() {
    this.modalRangeOpen = true;
  }

  closeDatePicker() {
    this.modalRangeOpen = false;
  }

  ngOnInit() {
    if (this.noValue) {
      this.start_value = null;
      this.end_value = null;
    }
  }



  getEventPath(evt) {
    const path = (evt.composedPath?.()) || evt.path;
    const { target } = evt;

    if (path != null) {
      // Safari doesn't include Window, but it should.
      return (path.indexOf(window) < 0) ? path.concat(window) : path;
    }

    if (target === window) {
      return [window];
    }

    function getParents(node, memo?) {
      memo = memo || [];
      const { parentNode } = node;

      if (!parentNode) {
        return memo;
      } else {
        return getParents(parentNode, memo.concat(parentNode));
      }
    }

    return [target].concat(getParents(target), window);
  }
  writeValue(v: any): void {
    if (v) {
      this.start_value = v.start;
      this.end_value = v.end;
      if (typeof v === 'string') {
        this.singleDateValue = this.options.format(v);
      } else {
        this.singleDateValue = v.state ? v.state.selectedDate : v;
      }
    }
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched() {}
}

export interface DateOptionsInterface {
  appendTo?: any;
  mode?: string;
  hilightedDate?: string;
  min?: number;
  max?: number;
  dayOffset?: string;
  lang?: Record<string, unknown>;
  format?: any;
}